import type { AxiosRequestOptions } from '@capture/client-api/src/orval/customInstance'
import type { RequiredCommonQueryParams } from '~/@types/backend-types'
import type { ServiceDict } from '~/API/externals'
import { getAuthToken, getStoredServiceDict } from '~/API/externals'
import { getCurrentLangDefinition, getDeviceProps } from '~/config/constants'
import { responseInterceptor } from '~/API/toolbox'

export type HostType = keyof ServiceDict

/**
 * Build common query params for API requests
 * Includes: API key, client version, device ID, app language
 * Optional: foreign-auth for accessing shared resources
 */
export function buildCommonParams(
    foreignAuth?: string,
): RequiredCommonQueryParams & {
    'foreign-auth'?: string
} {
    return {
        key: import.meta.env.VITE_API_KEY,
        client_v: import.meta.env.VITE_VERSION,
        device_id: getDeviceProps().device_id,
        app_lang: getCurrentLangDefinition().appLang,
        ...(foreignAuth && { 'foreign-auth': foreignAuth }),
    }
}

/**
 * Build axios request options for API requests
 * Includes: baseURL, Authorization header, response interceptors
 * @param hostType - Type of host (app, thumb, video, poll)
 * @param authToken - Optional auth token (defaults to getAuthToken())
 * @param services - Optional ServiceDict (defaults to getStoredServiceDict())
 */
export function buildRequestOptions(
    hostType: HostType = 'appHost',
    authToken?: string,
    services?: ServiceDict,
): AxiosRequestOptions {
    const token = authToken ?? getAuthToken()
    const serviceDict = services ?? getStoredServiceDict()

    if (!serviceDict) {
        throw new Error('Service dictionary not available')
    }

    const basePath = hostType === 'thumbHost' ? '/th' : '/st/4'

    return {
        baseURL: `https://${serviceDict[hostType]}${basePath}`,
        headers: {
            Authorization: `Basic ${btoa(`USER:${token}`)}`,
        },
        responseInterceptors: [responseInterceptor],
    }
}
